import { type ExpandedRichText, nonEmpty } from "@swo/shared";
import type { GeneralTranslation } from "@swo/storefront";
import type { NonEmptyArray } from "fp-ts/lib/NonEmptyArray";
import type React from "react";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import type { HeaderPinnedState } from "../../../components/HeaderComponent/subcomponents/use-header-pinned-state";
import type { Translated } from "../../../domain/translations/translations";
import { usePageLocaleContext } from "../../../global/page-context";
import { ComponentRenderer } from "../../_layout-helpers/component-renderer";
import { PDPDeliveryPackageDescription } from "../pdp-delivery-package-description";
import { type PDPSections, PDPSectionsWithNavigation } from "../pdp-navigation";
import { ProductDetailPageDownloadsCarousel } from "../PDP_DownloadsCarousel/ProductDetailPageDownloadsCarousel";
import { usePdpTranslations } from "../PdpTranslationsContext";
import type { BundlePageProps } from "../getDefaultVariantsSelection";
import { renderNonEmpty } from "../renderNonEmpty";
import type { ReviewTranslations } from "../reviewTranslations";

export const BDPBelowTheFoldLayout: React.FC<{
  data: Omit<BundlePageProps["data"], "header" | "footer">;
  gt: Translated<GeneralTranslation>;
  reviewsT: ReviewTranslations;
  primaryButton: React.JSX.Element;
  headerState: HeaderPinnedState;
  deliveryPackageProps: NonEmptyArray<string> | undefined;
  longDescription: ExpandedRichText | undefined;
  BundleContents: React.ReactNode;
}> = ({ data, gt, reviewsT, primaryButton, headerState, deliveryPackageProps, longDescription, BundleContents }) => {
  const t = usePdpTranslations();
  const { locale } = usePageLocaleContext();

  const { contentArea, downloads } = data.components;

  const sections: PDPSections = [
    {
      label: t("navigation.aboveTheFoldContentHeadline"),
      anchor: "root",
      mobileHidden: false,
    },
    contentArea.first.length > 0
      ? {
          label: t("navigation.contentAreaHeadline"),
          hidden: true,
          node: (
            <>
              {renderNonEmpty(contentArea.first, components => (
                <>
                  <Spacer size={"md"} />
                  <ComponentRenderer
                    components={components}
                    localeForNumberFormat={locale}
                    translations={gt}
                    noSpaceForFirstComponent={true}
                  />
                </>
              ))}
            </>
          ),
        }
      : null,
    deliveryPackageProps || longDescription
      ? {
          label: gt("moreInfoLabel"),
          node: (
            <PDPDeliveryPackageDescription
              gt={gt}
              deliveryPackageProps={deliveryPackageProps}
              longDescription={longDescription}
            />
          ),
        }
      : undefined,
    {
      label: t("bundleContents.navigationLabel"),
      node: BundleContents ? (
        <>
          <Spacer size={"md"} />
          {BundleContents}
        </>
      ) : null,
    },
    renderNonEmpty(downloads, downloads => ({
      label: t("downloadsHeadline"),
      node: <ProductDetailPageDownloadsCarousel downloadsHeadline={t("downloadsHeadline")} downloads={downloads} />,
    })),
    renderNonEmpty(contentArea.second, components => ({
      label: t("navigation.secondContentAreaHeadline"),
      hidden: true,
      node: (
        <>
          <Spacer size={"md"} />
          <ComponentRenderer
            components={components}
            localeForNumberFormat={locale}
            translations={gt}
            noSpaceForFirstComponent={true}
          />
        </>
      ),
    })),
  ].filter(nonEmpty);

  return <PDPSectionsWithNavigation headerState={headerState} Button={primaryButton} sections={sections} />;
};
